import { useRef, useState } from 'react'
import { isEqual } from 'date-fns'

import {
  EVENT_NAME_KEY_DOWN,
  EVENT_NAME_MOUSE_DOWN,
} from '../../../../../../../constants/event'
import { SELECT_DROPDOWN_ROLE } from '../../../../../../../constants/select'
import {
  useEventListener,
  useNotification,
  useTimeZone,
} from '../../../../../../../hooks'
import { isEqualNullable } from '../../../../../../../utils/common'
import {
  isEventKeyEscape,
  preventDefault,
} from '../../../../../../../utils/events'
import {
  getListingDateValue,
  parseListingDayInAppTimezone,
} from '../../../../../RentalPortal.utils'

export function useApplicantFollowUpEdit({ value, onSaveValue }) {
  const timezone = useTimeZone()

  const { handleAddErrorNotification } = useNotification()

  const [isEditing, setEditing] = useState(false)
  const [isSaving, setSaving] = useState(false)

  const [editValue, setEditValue] = useState(null)
  const [hasError, setError] = useState(false)

  const dateInputRef = useRef(null)
  const editContainerRef = useRef(null)

  const initialValue = parseListingDayInAppTimezone(value, timezone)

  function handleStartEditing() {
    setEditValue(initialValue)
    setError(false)
    setEditing(true)
  }

  function handleChangeEditValue(nextValue) {
    setEditValue(nextValue)
    setError(false)
  }

  async function handleSave(nextValue) {
    setSaving(true)

    try {
      await onSaveValue(nextValue)

      setEditing(false)
    } catch {
      setEditing(true)
    }

    setSaving(false)
  }

  async function handleDeleteValue() {
    return handleSave(null)
  }

  function handleSaveEditValue(event) {
    if (!isSaving) {
      if (editValue == null && dateInputRef.current?.value) {
        preventDefault(event)
        dateInputRef.current.focus()

        setError(true)
        handleAddErrorNotification(
          'RentalPortal.ApplicantProfileFollowUpInvalidDateErrorKey'
        )
      } else if (isEqualNullable(editValue, initialValue, isEqual)) {
        setEditing(false)
      } else {
        handleSave(getListingDateValue(editValue))
      }
    }
  }

  function handleDocumentMouseDown(event) {
    if (isEditing) {
      const isInsideFields = editContainerRef.current?.contains(event.target)

      const isInsideCalendar = event.target.closest?.(
        `[role="${SELECT_DROPDOWN_ROLE}"]`
      )

      if (!isInsideFields && !isInsideCalendar) {
        handleSaveEditValue(event)
      }
    }
  }

  function handleDocumentKeyDown(event) {
    if (isEditing && isEventKeyEscape(event)) {
      setEditing(false)
      setError(false)
    }
  }

  useEventListener({
    eventName: EVENT_NAME_MOUSE_DOWN,
    handler: handleDocumentMouseDown,
  })

  useEventListener({
    eventName: EVENT_NAME_KEY_DOWN,
    handler: handleDocumentKeyDown,
  })

  return {
    dateInputRef,
    editContainerRef,
    editValue,
    handleChangeEditValue,
    handleDeleteValue,
    handleStartEditing,
    hasError,
    isEditing,
    isSaving,
  }
}
